import { useEffect, useState } from "react";
import { Clock, Sun, CloudSun, Cloud, CloudFog, CloudRain, CloudSnow, CloudLightning } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

const weatherFor = (code) => {
  if (code === 0) return { Icon: Sun, label: "Cerah", color: "text-amber-500" };
  if ([1, 2].includes(code)) return { Icon: CloudSun, label: "Cerah berawan", color: "text-amber-400" };
  if (code === 3) return { Icon: Cloud, label: "Berawan", color: "text-slate-400" };
  if ([45, 48].includes(code)) return { Icon: CloudFog, label: "Berkabut", color: "text-slate-400" };
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return { Icon: CloudRain, label: "Hujan", color: "text-sky-500" };
  if ((code >= 71 && code <= 77) || [85, 86].includes(code)) return { Icon: CloudSnow, label: "Bersalju", color: "text-sky-300" };
  if (code >= 95) return { Icon: CloudLightning, label: "Badai petir", color: "text-indigo-500" };
  return { Icon: Cloud, label: "—", color: "text-slate-400" };
};

export default function LiveClock({ compact = false }) {
  const { settings, tzAbbrev } = useSettings();
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Cuaca Jakarta Pusat via Open-Meteo (gratis, tanpa API key), refresh tiap 15 menit.
  useEffect(() => {
    let active = true;
    const fetchW = () =>
      fetch("https://api.open-meteo.com/v1/forecast?latitude=-6.1805&longitude=106.8451&current_weather=true")
        .then((r) => r.json())
        .then((d) => { if (active && d?.current_weather) setWeather({ temp: Math.round(d.current_weather.temperature), code: d.current_weather.weathercode }); })
        .catch(() => {});
    fetchW();
    const t = setInterval(fetchW, 900000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const tz = settings.timezone || "Asia/Jakarta";
  const locale = settings.locale || "id-ID";
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(now);
  const date = new Intl.DateTimeFormat(locale, {
    timeZone: tz, weekday: "short", day: "2-digit", month: "short",
  }).format(now);
  const w = weather ? weatherFor(weather.code) : null;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-2 text-xs font-mono-num text-slate-700" data-testid="live-clock">
        <Clock size={12} className="text-emerald-600" />
        <span className="font-semibold">{time}</span>
        <span className="text-slate-400">{tzAbbrev}</span>
        {w && (
          <span className="inline-flex items-center gap-0.5 text-slate-500" data-testid="live-weather">
            <w.Icon size={12} className={w.color} />{weather.temp}°
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 pl-3 pr-3.5 py-1.5 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 shadow-sm" data-testid="live-clock">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center shadow-sm shrink-0">
        <Clock size={15} />
      </div>
      <div className="min-w-0">
        <div className="font-mono-num text-lg font-bold leading-tight text-slate-900 tabular-nums">
          {time} <span className="text-[10px] font-semibold text-emerald-600 align-middle">{tzAbbrev}</span>
        </div>
        <div className="text-[10px] text-slate-500 uppercase tracking-widest leading-tight">{date}</div>
      </div>
      {w && (
        <div className="flex items-center gap-1.5 pl-3 ml-1 border-l border-slate-200" data-testid="live-weather" title={`${w.label} · Jakarta Pusat`}>
          <w.Icon size={20} className={`${w.color} shrink-0`} />
          <div className="leading-none">
            <div className="font-mono-num text-base font-bold text-slate-800 tabular-nums">{weather.temp}°<span className="text-[10px] text-slate-400">C</span></div>
            <div className="text-[9px] text-slate-400 uppercase tracking-wide">Jakarta</div>
          </div>
        </div>
      )}
    </div>
  );
}
